const SIZE_ORDER = [
    "XXS",
    "XS",
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "2XL",
    "3XL",
    "4XL",
    "5XL",
    "6XL",
    "7XL",
    "8XL",
    "9XL"
];

export const SPECIAL_SIZE_WORDS = ["Regular"];

function specialSizeWordRank(label) {
    const normalized = label.trim().toLowerCase();
    return SPECIAL_SIZE_WORDS.findIndex((word) => word.toLowerCase() === normalized);
}

export function compareSearchSpringShoeWaistSizeLabels(aLabel, bLabel) {
    return parseFloat(aLabel) - parseFloat(bLabel);
}

function parseHeadStyleOrdinal(label) {
    const match = label.trim().match(/^(\d+)(?:\s+(\d+)\/(\d+))?$/);
    if (!match) {
        return null;
    }
    const whole = parseInt(match[1], 10);
    if (Number.isNaN(whole)) {
        return null;
    }
    if (!match[2]) {
        return whole;
    }
    const num = parseInt(match[2], 10);
    const den = parseInt(match[3], 10);
    if (Number.isNaN(num) || Number.isNaN(den) || den === 0) {
        return null;
    }
    return whole + num / den;
}

export function compareSearchSpringHeadSizeLabels(aLabel, bLabel) {
    const a = parseHeadStyleOrdinal(aLabel);
    const b = parseHeadStyleOrdinal(bLabel);
    if (a !== null && b !== null) {
        if (a === b) {
            return aLabel.localeCompare(bLabel);
        }
        return a - b;
    }
    return aLabel.localeCompare(bLabel);
}

export function compareSearchSpringFacetSizeLabels(aLabel, bLabel) {
    const sizeOrder = SIZE_ORDER;

    const aSpecial = specialSizeWordRank(aLabel);
    const bSpecial = specialSizeWordRank(bLabel);
    if (aSpecial !== -1 && bSpecial !== -1) {
        if (aSpecial === bSpecial) {
            return aLabel.localeCompare(bLabel);
        }
        return aSpecial - bSpecial;
    }
    if (aSpecial !== -1) {
        return -1;
    }
    if (bSpecial !== -1) {
        return 1;
    }

    const aIndex = sizeOrder.findIndex((size) => aLabel.startsWith(size));
    const bIndex = sizeOrder.findIndex((size) => bLabel.startsWith(size));

    if (aIndex !== -1 && bIndex !== -1) {
        if (aIndex === bIndex) {
            return aLabel.localeCompare(bLabel);
        }
        return aIndex - bIndex;
    }
    if (aIndex !== -1) {
        return -1;
    }
    if (bIndex !== -1) {
        return 1;
    }

    const aParts = aLabel.split("W x ");
    const bParts = bLabel.split("W x ");

    if (aParts.length === 2 && bParts.length === 2) {
        const aWaist = parseInt(aParts[0], 10);
        const aLength = parseInt(aParts[1], 10);
        const bWaist = parseInt(bParts[0], 10);
        const bLength = parseInt(bParts[1], 10);

        if (aLength !== bLength) {
            return aLength - bLength;
        }
        return aWaist - bWaist;
    }

    const aSize = parseInt(aLabel, 10);
    const bSize = parseInt(bLabel, 10);

    if (!isNaN(aSize) && !isNaN(bSize)) {
        return aSize - bSize;
    }

    return aLabel.localeCompare(bLabel);
}

export const PRODUCT_CARD_VARIANT_SIZE_KEYS = ["size", "waist_size", "shoe_size", "head_size"];

export function normalizeVariantSizeField(raw) {
    if (raw == null) {
        return null;
    }
    if (typeof raw === "string") {
        const t = raw.trim();
        return t.length ? t : null;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return String(raw);
    }
    if (Array.isArray(raw) && raw.length > 0) {
        return normalizeVariantSizeField(raw[0]);
    }
    return null;
}

export function resolveProductSizeAttributeKey(variants) {
    const row = variants?.[0];
    if (!row || typeof row !== "object") {
        return null;
    }
    const v = row;
    for (const key of PRODUCT_CARD_VARIANT_SIZE_KEYS) {
        if (Object.prototype.hasOwnProperty.call(v, key)) {
            return key;
        }
    }
    return null;
}

export function sizeLabelFromVariant(variant, sizeAttrName) {
    if (!sizeAttrName || !Object.prototype.hasOwnProperty.call(variant, sizeAttrName)) {
        return null;
    }
    return normalizeVariantSizeField(variant[sizeAttrName]);
}

export function collectUniqueVariantSizes(variants, sizeAttrName) {
    if (!variants?.length || !sizeAttrName) {
        return [];
    }
    const labels = variants
        .map((entry) =>
            typeof entry === "object" && entry != null
                ? sizeLabelFromVariant(entry, sizeAttrName)?.trim()
                : null
        )
        .filter((t) => Boolean(t));

    return [...new Set(labels)];
}

export function compareVariantSizeLabelsForSort(a, b, sizeAttrName) {
    const lowered = (sizeAttrName ?? "").toLowerCase();
    if (lowered.includes("size") && lowered.includes("head")) {
        return compareSearchSpringHeadSizeLabels(a, b);
    }
    if (lowered.includes("size") && (lowered.includes("shoe") || lowered.includes("waist"))) {
        return compareSearchSpringShoeWaistSizeLabels(a, b);
    }
    if (lowered.includes("size")) {
        return compareSearchSpringFacetSizeLabels(a, b);
    }
    return a.localeCompare(b);
}

/**
 * Typography for expanded card (“S”, “S–XL”, “9–11”) or "" when unavailable.
 */
export function buildExpandedProductSizesLine(variants) {
    const sizeAttrName = resolveProductSizeAttributeKey(variants ?? []);
    if (!sizeAttrName) {
        return "";
    }

    const labels = collectUniqueVariantSizes(variants ?? [], sizeAttrName);
    if (!labels.length) {
        return "";
    }

    const sorted = [...labels].sort((left, right) =>
        compareVariantSizeLabelsForSort(left, right, sizeAttrName)
    );

    if (sorted.length === 1) {
        return sorted[0];
    }
    return `${sorted[0]}–${sorted[sorted.length - 1]}`;
}
